<template>
    <div class="qr-scanner-container">
        <v-btn @click="toggleCamera" :color="showCamera ? 'error' : 'primary'" :disabled="isProcessing">
            {{ showCamera ? 'Annulla' : 'Scansiona codice' }}
        </v-btn>

        <!-- Indicatore timbrature offline in attesa -->
        <div v-if="pendingOfflineCount > 0" class="offline-pending-badge">
            <v-icon small color="warning">mdi-cloud-upload</v-icon>
            <span>{{ pendingOfflineCount }} timbratur{{ pendingOfflineCount > 1 ? 'e' : 'a' }} in attesa di
                sincronizzazione. Collegati a una rete e i dati verranno sincronizzati automaticamente.</span>
        </div>

        <!-- Riquadro sempre visibile -->
        <div class="camera-container">
            <div class="video-wrapper">
                <video v-show="showCamera" ref="videoPreview" id="videoPreview"></video>
                <div v-if="!showCamera && !isProcessing" class="placeholder-message">
                    <div class="placeholder-content">
                        <v-icon size="48" color="grey lighten-1">mdi-qrcode-scan</v-icon>
                        <p>Clicca su "<strong @click="toggleCamera">Scansiona codice</strong>" per avviare la
                            scansione del QR code.</p>
                    </div>
                </div>

                <div v-if="isProcessing" class="processing-overlay">
                    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                </div>
                <div v-if="successFeedback" class="success-overlay"></div>
            </div>
            <p v-if="scanningError" class="error-message">{{ scanningError }}</p>
        </div>

        <p class="result-message">
            <span v-html="datiRegistrati"></span>
        </p>

        <v-row justify="center" class="mt-4">
            <v-col cols="12" sm="6">
                <v-btn block large :color="entrataUscita === 'E' ? 'success' : 'gray'"
                    @click="salvaTimbratura('entrata')">
                    <v-icon left>login</v-icon>
                    Entrata
                </v-btn>
            </v-col>

            <v-col cols="12" sm="6">
                <v-btn block large :color="entrataUscita === 'U' ? 'success' : 'gray'"
                    @click="salvaTimbratura('uscita')">
                    <v-icon left>logout</v-icon>
                    Uscita
                </v-btn>
            </v-col>
        </v-row>

    </div>
</template>

<script>
import { BrowserMultiFormatReader } from '@zxing/library';

import { valida, getStatus, validaOffline } from "@/api/api.timbratura.js"

const STORAGE_KEY = 'timbrature_offline';

export default {
    data() {
        return {
            qrResult: null,
            codeReader: new BrowserMultiFormatReader(),
            showCamera: false,
            scanningError: null,
            successFeedback: false,
            isProcessing: false,
            datiRegistrati: "",

            // Gestione offline
            isOnline: navigator.onLine,
            pendingOfflineCount: 0,
            syncInterval: null,
            isSyncing: false,

            entrataUscita: 'E'  // E = entrata, U = uscita
        };
    },

    async mounted() {
        this.showCamera = false;
        console.log("Lettura stato");
        await this.getStato();
        console.log("Attesa apertura camera...");

        // Aspetto qualche millisecondo per evitare che la camera si apra prima di aver caricato i dati dell'utente
        await new Promise(resolve => setTimeout(resolve, 100));

        console.log("Apertura camera...");
        await this.checkAndOpenCamera();
        // Inizializza gestione offline
        this.initOfflineManagement();
    },

    computed: {
        nomeDipendente() {
            return this.$store.getters["user/getNomeDipendente"];
        },

        codiceBadge() {
            return this.$store.getters["user/getCodiceBadge"];
        },
    },

    watch: {
        async codiceBadge(newVal) {
            if (newVal) await this.getStato();
        },

        async nomeDipendente(newVal) {
            if (newVal) await this.getStato();
        }
    },

    methods: {
        //#region  ========== GESTIONE OFFLINE ==========

        initOfflineManagement() {
            // Conta le timbrature in attesa
            this.updatePendingCount();

            // Listener per eventi online/offline
            window.addEventListener('online', this.handleOnline);
            window.addEventListener('offline', this.handleOffline);

            // Se è online, avvia il processo di sincronizzazione
            if (this.isOnline) {
                this.startSyncProcess();
            }
        },

        handleOnline() {
            this.isOnline = true;
            this.datiRegistrati = '<span style="color: green;">✓ Connessione ripristinata. Sincronizzazione in corso...</span>';
            this.startSyncProcess();
        },

        handleOffline() {
            this.isOnline = false;
            this.stopSyncProcess();
        },

        startSyncProcess() {
            // Prova subito a sincronizzare
            this.syncOfflineTimbrature();

            // Imposta tentativo ogni minuto
            if (!this.syncInterval) {
                this.syncInterval = setInterval(() => {
                    this.syncOfflineTimbrature();
                }, 60000); // 60 secondi
            }
        },

        stopSyncProcess() {
            if (this.syncInterval) {
                clearInterval(this.syncInterval);
                this.syncInterval = null;
            }
        },

        getOfflineTimbrature() {
            try {
                const data = localStorage.getItem(STORAGE_KEY);
                return data ? JSON.parse(data) : [];
            } catch (error) {
                console.error('Errore nel recupero timbrature offline:', error);
                return [];
            }
        },

        saveOfflineTimbratura(timbratura) {
            try {
                const timbrature = this.getOfflineTimbrature();
                timbrature.push(timbratura);
                localStorage.setItem(STORAGE_KEY, JSON.stringify(timbrature));
                this.updatePendingCount();
            } catch (error) {
                console.error('Errore nel salvataggio timbratura offline:', error);
            }
        },

        clearOfflineTimbrature() {
            try {
                localStorage.removeItem(STORAGE_KEY);
                this.updatePendingCount();
            } catch (error) {
                console.error('Errore nella pulizia timbrature offline:', error);
            }
        },

        updatePendingCount() {
            const timbrature = this.getOfflineTimbrature();
            this.pendingOfflineCount = timbrature.length;
        },

        async syncOfflineTimbrature() {
            // Evita sincronizzazioni multiple simultanee
            if (this.isSyncing || !this.isOnline) {
                return;
            }

            const timbrature = this.getOfflineTimbrature();

            // Se non ci sono timbrature da sincronizzare, ferma il processo
            if (timbrature.length === 0) {
                this.stopSyncProcess();
                return;
            }

            this.isSyncing = true;

            try {
                // Ordina per timestamp (dalla più vecchia alla più recente)
                timbrature.sort((a, b) => a.timestamp - b.timestamp);

                let successCount = 0;
                let failedTimbrature = [];

                // Elabora sequenzialmente ogni timbratura
                for (const timbratura of timbrature) {
                    try {
                        const par = {
                            Timestamp: timbratura.timestamp,
                            Token: timbratura.qrContent,
                            NomeDipendente: timbratura.nomeDipendente,
                            CodiceBadge: timbratura.codiceBadge,
                            IdStazione: "Battisti",
                            Fingerprint: this.getDeviceFingerprint(),
                            EntrataUscita: this.entrataUscita,
                        };

                        await validaOffline(par);
                        successCount++;

                    } catch (error) {
                        console.error('Errore nella sincronizzazione timbratura:', error);
                        // Salva la timbratura fallita per ritentare
                        failedTimbrature.push(timbratura);
                    }
                }

                // Aggiorna lo storage con solo le timbrature fallite
                if (failedTimbrature.length > 0) {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(failedTimbrature));
                } else {
                    // Tutte le timbrature sono state sincronizzate con successo
                    this.clearOfflineTimbrature();
                    this.stopSyncProcess();
                }

                this.updatePendingCount();

                // Notifica il risultato
                if (successCount > 0) {
                    this.datiRegistrati = `<span style="color: green;">✓ ${successCount} timbratura${successCount > 1 ? 'e' : ''} sincronizzata${successCount > 1 ? 'e' : ''} con successo!</span>`;
                }

                if (failedTimbrature.length > 0) {
                    this.datiRegistrati = `<span style="color: orange;">⚠ ${successCount} sincronizzata${successCount > 1 ? 'e' : ''}, ${failedTimbrature.length} in attesa. Nuovo tentativo tra 1 minuto.</span>`;
                }

            } catch (error) {
                console.error('Errore generale nella sincronizzazione:', error);
            } finally {
                this.isSyncing = false;
            }
        },

        //#endregion

        //#region  ========== GESTIONE CAMERA E SCANSIONE ==========

        async checkAndOpenCamera() {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const hasCamera = devices.some(device => device.kind === 'videoinput');
                if (hasCamera) {
                    await this.openCamera();
                }
            } catch (err) {
                console.error("Errore nel controllo dei permessi:", err);
            }
        },

        async toggleCamera() {
            if (this.showCamera) {
                this.closeCamera();
            } else {
                await this.openCamera();
            }
        },

        async openCamera() {
            this.showCamera = true;
            this.qrResult = null;
            this.scanningError = null;

            try {
                await this.codeReader.decodeFromVideoDevice(
                    undefined,
                    this.$refs.videoPreview,
                    (result, err) => {
                        if (result) {
                            this.isProcessing = true;
                            this.qrResult = result.text;
                            this.successFeedback = true;
                            setTimeout(() => this.successFeedback = false, 1500);
                            this.registraTimbratura(result.text);
                            this.isProcessing = false;
                            this.closeCamera();
                        }
                        if (err && !(err.name === 'NotFoundException')) {
                            console.error(err);
                        }
                    },
                    {
                        delayBetweenScanAttempts: 100,
                        delayBetweenScanSuccess: 500,
                    }
                );
            } catch (err) {
                this.scanningError = "Impossibile accedere alla fotocamera: " + err.message;
                this.showCamera = false;
                this.isProcessing = false;
            }
        },

        closeCamera() {
            this.codeReader.reset();
            this.showCamera = false;
        },

        //#endregion

        async registraTimbratura(qrContent) {
            // Se ci sono timbrature offline in attesa, sincronizzale prima
            if (this.pendingOfflineCount > 0 && this.isOnline) {
                await this.syncOfflineTimbrature();
            }

            // Verifica se è online
            if (!this.isOnline) {
                // Modalità offline: salva nello storage
                const offlineTimbratura = {
                    timestamp: Date.now(),
                    qrContent: qrContent,
                    nomeDipendente: this.nomeDipendente,
                    codiceBadge: this.codiceBadge,
                };

                this.saveOfflineTimbratura(offlineTimbratura);

                // Mostra messaggio di conferma offline
                this.datiRegistrati = `
                    <div style="background-color: #fff3cd; padding: 15px; border-radius: 8px; border-left: 4px solid #ffc107;">
                        <strong style="color: #856404;">⚠ Sistema Offline</strong><br/>
                        <span style="color: #856404;">
                            Timbratura salvata sul tuo telefono.<br/>
                            Sarà sincronizzata automaticamente quando la connessione sarà ripristinata.
                        </span>
                    </div>
                `;

                return;
            }

            // Modalità online: chiamata normale
            try {
                let par = {
                    Token: qrContent,
                    IdStazione: "Battisti",
                    Fingerprint: this.getDeviceFingerprint(),
                    NomeDipendente: this.nomeDipendente,
                    CodiceBadge: this.codiceBadge,
                    EntrataUscita: this.entrataUscita
                };
                let res = await valida(par);
                this.datiRegistrati = res.data;

                if (this.datiRegistrati.includes("Attività registrata"))
                    this.getStato();
            } catch (error) {
                console.error('Errore nella registrazione timbratura online:', error);

                // Se l'errore è di rete, salva offline
                if (!navigator.onLine || error.message.includes('Network') || error.message.includes('timeout')) {
                    const offlineTimbratura = {
                        timestamp: Date.now(),
                        qrContent: qrContent,
                        nomeDipendente: this.nomeDipendente,
                        codiceBadge: this.codiceBadge,
                    };

                    this.saveOfflineTimbratura(offlineTimbratura);
                    this.handleOffline();

                    this.datiRegistrati = `
                        <div style="background-color: #fff3cd; padding: 15px; border-radius: 8px; border-left: 4px solid #ffc107;">
                            <strong style="color: #856404;">⚠ Errore di Connessione</strong><br/>
                            <span style="color: #856404;">
                                Timbratura salvata sul tuo telefono.<br/>
                                Sarà sincronizzata automaticamente quando la connessione sarà ripristinata.
                            </span>
                        </div>
                    `;
                } else {
                    // Altro tipo di errore
                    this.datiRegistrati = `<span style="color: red;">✗ Errore: ${error.message}</span>`;
                }
            }
        },

        getDeviceFingerprint() {
            const userAgent = window.navigator.userAgent;
            const screenResolution = `${window.screen.width}x${window.screen.height}`;
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const language = window.navigator.language;
            const plugins = Array.from(window.navigator.plugins || []).map(p => p.name).join('|');

            let canvasFingerprint = this.getCanvasFingerprint();

            return {
                userAgent,
                screenResolution,
                timezone,
                language,
                plugins,
                canvasFingerprint,
            };
        },

        getCanvasFingerprint() {
            let canvas = document.createElement('canvas');
            let ctx = canvas.getContext('2d');
            ctx.textBaseline = 'top';
            ctx.font = "14px 'Arial'";
            ctx.textBaseline = 'alphabetic';
            ctx.fillStyle = '#f60';
            ctx.fillRect(125, 1, 62, 20);
            ctx.fillStyle = '#069';
            ctx.fillText('Cwm fjordbank glyphs vext quiz, 😃', 2, 15);
            ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
            ctx.fillText('Cwm fjordbank glyphs vext quiz, 😃', 4, 17);
            return canvas.toDataURL();
        },

        async getStato() {
            try {
                let par = {
                    IdStazione: "Battisti",
                    Fingerprint: this.getDeviceFingerprint(),
                    NomeDipendente: this.nomeDipendente,
                    CodiceBadge: this.codiceBadge,
                };
                let res = await getStatus(par);
                this.datiRegistrati = res.data.message;
                this.entrataUscita = res.data.entrataUscita;
            } catch (error) {
                console.error('Errore nel recupero stato:', error);
                // Non mostrare errore se offline, è normale
                if (this.isOnline) {
                    this.datiRegistrati = '<span style="color: red;">Errore nel recupero dello stato</span>';
                }
            }
        },

        salvaTimbratura(tipo) {
            if (tipo == 'entrata')
                this.entrataUscita = "E";
            else this.entrataUscita = "U";
        },
    },

    beforeDestroy() {
        this.closeCamera();

        // Rimuovi listener
        window.removeEventListener('online', this.handleOnline);
        window.removeEventListener('offline', this.handleOffline);

        // Ferma sincronizzazione
        this.stopSyncProcess();
    },
};
</script>

<style scoped>
.qr-scanner-container {
    margin-top: 2em;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
}

.offline-pending-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: #fff3cd;
    color: #856404;
    padding: 10px 20px;
    border-radius: 8px;
    border: 1px solid #ffc107;
    font-weight: 500;
    animation: pulse-warning 2s infinite;
}

@keyframes pulse-warning {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.8;
    }
}

.camera-container {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.video-wrapper {
    position: relative;
    width: 300px;
    height: 300px;
    border: 2px solid #ccc;
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
}

video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.processing-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
}

.success-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 255, 0, 0.3);
    border-radius: 12px;
    pointer-events: none;
}

.error-message {
    color: red;
    margin-top: 10px;
}

.result-message {
    margin-top: 10px;
    max-width: 500px;
    text-align: center;
    margin-left: 4px;
    margin-right: 4px;
}

.placeholder-message {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(245, 245, 245, 0.9);
    border-radius: 12px;
    text-align: center;
    padding: 20px;
}

.placeholder-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
}

.placeholder-content p {
    margin: 0;
    color: #666;
    font-size: 16px;
}
</style>